import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType, Redirect } from 'next';
import absoluteUrl from 'next-absolute-url';

import { AppLayout } from '@src/components/layouts/app-layout';
import { MyTrips } from '@src/components/pages/my-trips';
import { MyTripsProps } from '@src/components/pages/my-trips/types';
import { getProfileCache } from '@src/features/profile/lib/profile-cache';
import { SiteLocale } from '@src/generated/dato-cms';
import { Locale, i18nConfig } from '@src/i18n/locales';
import { createSsrServices } from '@src/lib/ssr/create-ssr-services';
import { getCurrentTrip, getKeyExpiredRedirect } from '@src/lib/ssr/get-trip-or-key-expired';
import { TripsData } from '@src/lib/ssr/types';
import { withClientIp } from '@src/lib/ssr/with-client-ip';
import { isNativeDomain, isWhiteLabel } from '@src/services/content/branding/utils';
import { getBrandingService, getContentService } from '@src/services/graphql';
import { getAuthToken, hasProfileToken } from '@src/utils/auth-token';
import { getRoomPageUrl } from '@src/utils/urls';
import { isAppWebView, isKioskWebView } from '@src/utils/user-agent';

type GetTripsResult = TripsData | { redirect: Redirect };

const getTrips = async (req: GetServerSidePropsContext['req'], locale: string): Promise<GetTripsResult> => {
  const token = getAuthToken(req);

  // Not authenticated
  if (!token) {
    return {
      trips: [],
      currentTrip: null,
    };
  }

  // Authenticated via key, only fetch currentTrip
  if (!hasProfileToken(req)) {
    const result = await getCurrentTrip({ token, locale });

    const keyExpiredRedirect = getKeyExpiredRedirect(result);
    if (keyExpiredRedirect) return keyExpiredRedirect;

    return {
      currentTrip: result.ok ? result.data : null,
      trips: [],
    };
  }

  const { tripService } = createSsrServices(token);

  // Authenticated via profile token
  return {
    trips: await tripService.getUpcomingTrips(locale),
    currentTrip: null,
  };
};

export const getServerSideProps: GetServerSideProps<MyTripsProps> = withClientIp(async ({ req, params }) => {
  const locale = (params?.locale || i18nConfig.defaultLocale) as Locale;

  const { hostname } = new URL(absoluteUrl(req).origin);

  const [contentService, brandingService] = await Promise.all([getContentService(), getBrandingService()]);
  const [commonContent, tripsResult, brand] = await Promise.all([
    contentService.getCommonContent(locale as SiteLocale),
    getTrips(req, locale),
    brandingService.getBrandByHost(hostname, locale as SiteLocale),
  ]);

  if ('redirect' in tripsResult) {
    return tripsResult;
  }

  const { trips, currentTrip } = tripsResult;

  const propertyIds = [currentTrip?.property.id, ...trips.map(({ property }) => property.id)]
    .filter((id): id is string => Boolean(id))
    .sort();
  const unitGroupIds = [currentTrip?.unitGroup.id, ...trips.map(({ unitGroup }) => unitGroup.id)]
    .filter((id): id is string => Boolean(id))
    .sort();

  const { properties, unitGroups } = await contentService.getMyTripsPage({
    propertyIds,
    unitGroupIds,
    locale: locale as SiteLocale,
  });

  const tripsWithRoomUrls = trips.map((trip) => {
    const property = properties.find(({ id }) => id === trip.property.id)!;
    const unitGroup = unitGroups.find(({ id }) => id === trip.unitGroup.id)!;

    return {
      ...trip,
      property,
      unitGroup,
      roomPageUrl:
        getRoomPageUrl({
          websiteUrl: brand.websiteUrl,
          countrySlug: property.country.slug,
          citySlug: property.city.slug,
          districtSlug: property.district.slug,
          propertySlug: property.slug,
          unitGroupSlug: unitGroup.slug,
        }) ?? '',
    };
  });
  const currentTripListItem: MyTripsProps['currentTrip'] = currentTrip
    ? {
        ...currentTrip,
        property: properties.find(({ id }) => id === currentTrip.property.id)!,
        unitGroup: unitGroups.find(({ id }) => id === currentTrip.unitGroup.id)!,
        accessInformation: currentTrip.accessInformationV2,
      }
    : null;

  return {
    props: {
      currentTrip: currentTripListItem,
      trips: tripsWithRoomUrls,
      brand,
      isWhiteLabel: isWhiteLabel(brand.ibeHost),
      isNativeBrand: isNativeDomain(brand.ibeHost),
      cmsContent: commonContent,
      profileCache: getProfileCache(req) ?? null,
      isAppWebView: isAppWebView(req.headers['user-agent']),
      isKiosk: isKioskWebView(req.headers['user-agent']),
    },
  };
});

type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const TripsPage = (pageProps: PageProps) => <MyTrips {...pageProps} />;
TripsPage.Layout = AppLayout;
export default TripsPage;
